"use client";

import clsx from "clsx";
import type { ReactNode } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { Icon } from "./icons";

/**
 * A toolbar menu (layers, grid) on phones (`phone:`, also held sideways) as a window in the middle of the screen, as the
 * app's other windows: the screen's width less a margin, at most 80 % of its height, scrolling in itself. Elsewhere the
 * menu keeps its own place (its other classes).
 */
export const PHONE_WINDOW =
  "phone:fixed phone:inset-x-3 phone:top-1/2 phone:bottom-auto phone:z-50 phone:mt-0 phone:w-auto phone:max-h-[80dvh] phone:-translate-y-1/2 phone:rounded-xl";

/** The dimmed page behind a popup on phones; a tap on it closes the popup. */
export function PhoneBackdrop({ onClose }: { onClose: () => void }) {
  return <div aria-hidden className="fixed inset-0 z-40 bg-black/50 wide:hidden" onMouseDown={onClose} />;
}

/** A window's close button: the ✕ at the end of its head (`className`: its place). */
export function CloseButton({ onClose, className }: { onClose: () => void; className?: string }) {
  const { t } = useI18n();
  return (
    <button type="button" onClick={onClose} aria-label={t("action.close")} className={clsx("inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted hover:bg-surface-2 hover:text-ink", className)}>
      <Icon name="x" className="h-4 w-4" />
    </button>
  );
}

/** A popup's title and its close button (phones only). */
export function PhoneHead({ title, info, onClose }: { title: string; info?: ReactNode; onClose: () => void }) {
  return (
    <div className="flex items-center justify-between px-2 pt-1 pb-2 wide:hidden">
      <span className="flex items-center gap-1 text-[14px] font-semibold">
        {title}
        {info}
      </span>
      <CloseButton onClose={onClose} />
    </div>
  );
}
